import {
    AttributeDefinitionType,
    AttributesData,
    ConversationsDataType,
    TaskMode,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import {
    AttributeGroupConfigExtended,
    AttributeGroupEvent,
    BaseAttributeGroup,
} from './BaseAttributeGroup'
import { AllowedAttributeKeys, AttributeValueType } from 'Types/Attribute.types'
import { FormInstance } from 'antd/lib'
import { createReactElement } from 'shared/utils/react.utils'
import { AttributesFormWrapper } from 'Components/tasks/Attributes/AttributesFormWrapper'

export class TurnsAttributeGroup extends BaseAttributeGroup {
    attributeDefinitionType = AttributeDefinitionType.TURN_EVALUATION as const
    constructor(attributeContext: AttributeContext, config: AttributeGroupConfigExtended) {
        super(attributeContext, config)
        this.build()
    }

    public build(): void {
        const draftOutputContainer = this.attributeContext.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }

        const data = draftOutputContainer.data.data as ConversationsDataType

        const config = this.attributeContext.taskConfig

        const props = {
            groupKey: this.groupKey,
            attributes: this.attributes,
            onUpdateAttribute: (attrName: string, val: AttributeValueType) => {
                const index = Number(this.groupKey.split('-').at(-1))
                const turnAnnotations = data.response.main_info.value.turn_annotations
                const attributes = turnAnnotations?.[index]?.attributes ?? {}

                if (val === undefined) {
                    if (attributes[attrName]) {
                        delete attributes[attrName]
                    }
                } else {
                    if (attributes[attrName]) {
                        attributes[attrName].data.value = val
                    } else {
                        turnAnnotations[index] = {
                            ...turnAnnotations[index],
                            attributes: {
                                ...attributes,
                                [attrName]: {
                                    data: {
                                        value: val,
                                        value_type: this.attributes.find(
                                            attr => attr.name === attrName
                                        )?.value_definition.value_type as AllowedAttributeKeys,
                                    },
                                    value_id: crypto.randomUUID(),
                                },
                            },
                        }
                    }
                }

                this.validate()
                this.emit(AttributeGroupEvent.Change)
                this.render()
            },
            getValues: this.getAttributeValues,
            attributesOnUI: this.attributeContext.attributesOnUI,
            setForm: (form: FormInstance) => {
                this.form = form
            },
        }

        this.element = createReactElement(AttributesFormWrapper, {
            ...props,
            isReadOnly: () => this.isReadOnly(),
            headerComponent:
                this.props?.header ?? this.attributeContext.task.createAttributeHeader(),
            getFeedback: this.attributeContext.task.contextHandler.getFeedback,
            mode: config.mode,
            isTrainingMode: this.attributeContext.task.contextHandler.isTrainingMode,
            wrapperClassName: 'turn-attributes',
        })
    }

    private getAttributeValues = () => {
        const data = this.attributeContext.taskState?.getDraftOutputContainer()?.data
            .data as ConversationsDataType
        const index = Number(this.groupKey.split('-').at(-1))
        const turnAttributes =
            data.response.main_info.value.turn_annotations[index]?.attributes ?? {}

        return Object.keys(turnAttributes).reduce((acc, key) => {
            acc[key] = {
                data: turnAttributes[key].data,
            }
            return acc
        }, {} as AttributesData)
    }

    validate = async () => {
        let isValidData = true

        try {
            await this.form?.validateFields()
        } catch {
            isValidData = false
        }

        if (isValidData) {
            this.attributeContext.studio.inputManager.removeViolation('turn-attributes', 'error')
        } else {
            this.attributeContext.studio.inputManager.addViolation({
                id: 'turn-attributes',
                type: 'error',
                message: 'Please fill all the turn level attributes',
            })
        }

        return isValidData
    }

    private isReadOnly = () => {
        const config = this.attributeContext.taskConfig

        if (config.mode === TaskMode.Triager) {
            return false
        } else if (config.mode === TaskMode.Maker || config.mode === TaskMode.Rework) {
            return this.attributeContext.task.isReadOnly || this.disabled
        } else {
            return this.attributeContext.task.isReadOnly
        }
    }

    removeAllAttributes() {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as ConversationsDataType
        if (!data) {
            return
        }
        const index = Number(this.groupKey.split('-').at(-1))
        if (data.response.main_info.value.turn_annotations[index]) {
            data.response.main_info.value.turn_annotations[index].attributes = {}
        }
    }
}
